import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useKeyDown } from '../hooks/key';
import useSwipe from '../hooks/swipe';
import { formatDate } from '../utils/date';

type Board = Array<Array<number>>;
type GameState = 'loading' | 'active' | 'finished';
type Props = {
    dimensions: {
        width: number,
        height: number
    }
};

function* chunk<T>(array: T[], size: number): Generator<T[], void> {
    for (let x = 0, l = array.length; x < l; x += size) {
        yield array.slice(x, size + x);
    }
}

const Classic = ({ dimensions }: Props): JSX.Element => {
    const defaultSize = 4;
    const sizes = [3, 4, 5, 6, 7, 8];
    const [board, setBoard] = useState<Board>([...chunk([...Array(defaultSize * defaultSize).keys()], defaultSize)]);
    const [gameState, setGameState] = useState<GameState>('loading');
    const [size, setSize] = useState<number>(4);
    const [startTime, setStartTime] = useState<number>(0);
    const [finishTime, setFinishTime] = useState<number>(0);
    const [turns, setTurns] = useState<number>(0);
    const cellSize = useMemo<number>(() => {
        const maxWidth = Math.floor((Math.min(80 * size, dimensions.width - 10 - (size - 1) * 5)) / size);
        const maxHeight = Math.floor((Math.min(80 * size, dimensions.height - 275 - (size - 1) * 5)) / size);

        return Math.min(maxWidth, maxHeight);
    }, [dimensions, size]);

    const isPuzzleSolved = useCallback((board: number[], size: number): boolean => {
        const check = [...board];

        check[check.indexOf(0)] = size * size;

        return check.every((value: number, index: number, board: number[]): boolean => {
            return !(board[index + 1] && board[index] > board[index + 1]);
        });
    }, []);

    const createBoard = useCallback((size: number): void => {
        const unshuffled = [...Array(size * size).keys()];
        let validPuzzle = false;
        let shuffled: number[] = [];

        const isValid = (array: Array<number>): boolean => {
            let inversions = 0;

            // This can be calculated in a less complex manner with a slightly modified mergesort
            // But this method suffices for this requirement
            for (let x = 0, l = array.length; x < l - 1; x++) {
                for (let y = x + 1; y < l; y++) {
                    if (array[x] && array[y] && array[x] > array[y]) {
                        inversions++;
                    }
                }
            }

            if (size & 1) {
                return ! (inversions & 1);
            }

            const blankPosition = size - (Math.floor(array.indexOf(0) / size));

            if (blankPosition & 1) {
                return ! (inversions & 1); 
            }

            return !! (inversions & 1);
        }

        while (! validPuzzle) {
            shuffled = unshuffled
                .map(value => ({ value, sort: Math.random() }))
                .sort((a, b) => a.sort - b.sort)
                .map(({ value }) => value);

            validPuzzle = isValid(shuffled) && ! isPuzzleSolved(shuffled, size);
        }

        const board: Board = [...chunk<number>(shuffled, size)];

        setBoard(board);
        setGameState('active');
    }, [isPuzzleSolved]);

    /*useEffect(() => {
        switch (gameState) {
            case 'start':
                setBoard(createOrderedBoard(size));
            break;
            case 'open':
                timer.current = window.setTimeout(() => {
                    setGameState('flip');
                }, 1);
            break;
            case 'flip':
                timer.current = window.setTimeout(() => {
                    setGameState('build');
                }, ((2 * size - 2) * animationDelay + animationStart + animationDuration) * 1000);
            break;
            case 'build':
                // Technically this step could be done in the flip state's completion, but this way the animation
                // is cleared from the element so there aren't any issues with the timings
                createBoard(size);

                timer.current = window.setTimeout(() => {
                    setGameState('unflip');
                }, 1);
            break;
            case 'unflip':
                timer.current = window.setTimeout(() => {
                    setGameState('active');
                }, ((2 * size - 2) * animationDelay + animationStart / 2 + animationDuration) * 1000);
            break;
        }

        return () => {
            if (timer.current) {
                clearInterval(timer.current);
            }
        }
    }, [createBoard, gameState, size]);*/

    const resetGame = useCallback(() => {
        setTurns(0);
        setStartTime(0);
        createBoard(size);
        setGameState('active');
    }, [createBoard, size]);

    useEffect(() => {
        resetGame();
    }, [resetGame]);

    const getCellState = useCallback((row: number, cell: number): string => {
        const correctValue = row * size + cell + 1;
        
        if (board[row][cell] === correctValue) {
            return 'cell-outer correct';
        }

        return 'cell-outer';
    }, [board, size]);

    const swapCells = (row: number, cell: number, targetRow: number, targetCell: number): void => {
        const updatedBoard = [...board];
        const now = Date.now();
        const currentMoves = turns + 1;

        [updatedBoard[row][cell], updatedBoard[targetRow][targetCell]] = [board[targetRow][targetCell], board[row][cell]];

        if (turns === 0) {
            setStartTime(now);
        }

        setBoard(updatedBoard);
        setTurns(currentMoves);

        if (isPuzzleSolved(board.flat(), size)) {
            const finishTime = now - (startTime ? startTime : now);
            const results = JSON.parse(localStorage.getItem('classic') || '{}');

            if (results[size]) {
                results[size].time = Math.min(results[size].time, finishTime);
                results[size].turns = Math.min(results[size].turns, currentMoves);
            } else {
                results[size] = {time: finishTime, turns: currentMoves};
            }

            localStorage.setItem('classic', JSON.stringify(results));

            setGameState('finished');
            setFinishTime(finishTime);
        }
    };

    const doDownAction = (key: string): void => {
        if (key === 'Escape') {
            resetGame();

            return;
        }

        if (gameState !== 'active') {
            return;
        }

        const blankPosition = board.flat().indexOf(0);
        const row = Math.floor(blankPosition / size);
        const cell = blankPosition % size;

        switch (key) {
            case 'a':
            case 'ArrowLeft':
                if (cell < size - 1 && board[row][cell + 1]) {
                    swapCells(row, cell + 1, row, cell);
                }
            break;
            case 'd':
            case 'ArrowRight':
                if (cell > 0 && board[row][cell - 1]) {
                    swapCells(row, cell - 1, row, cell);
                }
            break;
            case 'w':
            case 'ArrowUp':
                if (row < size - 1 && board[row + 1][cell]) {
                    swapCells(row + 1, cell, row, cell);
                }
            break;
            case 's':
            case 'ArrowDown':
                if (row > 0 && board[row - 1][cell]) {
                    swapCells(row - 1, cell, row, cell);
                }
            break;
        }
    };

    useKeyDown(doDownAction, ['w', 'a', 's', 'd', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Escape']);

    const swipeHandlers = useSwipe({
        left: () => doDownAction('a'),
        right: () => doDownAction('d'),
        top: () => doDownAction('w'),
        bottom: () => doDownAction('s')
    });

    const handleClick = (event: React.MouseEvent<HTMLDivElement>, row: number, cell: number): void => {
        if (! board[row][cell]) {
            return;
        }

        if (gameState !== 'active') {
            return;
        }

        if (cell > 0 && !board[row][cell - 1]) {
            swapCells(row, cell, row, cell - 1);
        } else if (cell < board[row].length - 1 && !board[row][cell + 1]) {
            swapCells(row, cell, row, cell + 1);
        } else if (row > 0 && !board[row - 1][cell]) {
            swapCells(row, cell, row - 1, cell);
        } else if (row < board.length - 1 && !board[row + 1][cell]) {
            swapCells(row, cell, row + 1, cell);
        }
    };

    return (
        <div id="game"{...swipeHandlers} className="classic">
            <div id="title">{size * size - 1} Number Puzzle</div>
            <div className="control">
                <select id="size" defaultValue={size} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setSize(parseInt(event.target.value))}>
                 {sizes.map(s => <option key={s} value={s}>{s} x {s}</option>)}
                </select>
            </div>
            <div id="board" className={`grid-${size} classic ${gameState}`} style={{'--cell-size': `${cellSize}px`} as React.CSSProperties}>
                {board.map((row, rowKey) => (
                    <Fragment key={rowKey}>
                    {row.map((cell, cellKey) => (
                        <div 
                            key={cellKey} 
                            className={getCellState(rowKey, cellKey)}
                            onClick={(event: React.MouseEvent<HTMLDivElement>) => handleClick(event, rowKey, cellKey)}>
                            <div className="cell-front">{cell > 0 && cell}</div>
                        </div>
                    ))}
                    </Fragment>
                ))}
            </div>
            {gameState !== 'finished' && <div id="moves">
                <span className="count">{turns}</span> {turns === 1 ? 'move' : 'moves'}
            </div>}
            {gameState === 'finished' && <div id="win-outer">
                <div id="win-info">It took you <strong>{turns} moves</strong> in <strong>{formatDate(finishTime)}</strong></div>
                <button className="button play" onClick={(event: React.MouseEvent<HTMLButtonElement>) => resetGame()}>Play Again</button>
            </div>}
        </div>
    )
}

export default Classic;
